import {Fragment, useState} from 'react'
import { CheckIcon, MinusIcon, PlusIcon, InformationCircleIcon } from '@heroicons/react/16/solid'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import {RetainerImage} from "../../../assets";
import { Show, SignInButton, SignUpButton, UserButton } from '@clerk/react'

export const tiers = [
    {
        id: "Starter",
        key: "launch",
        name: 'SEO Foundation',
        description: 'Local businesses, early-stage, simple sites',
        priceMonthly: <div className="">$997</div>,
        cost: "997",
        href: '/checkout?index=launch',
        imageSrc: "https://images.pexels.com/photos/4050081/pexels-photo-4050081.jpeg",
        imageAlt: "HerStory SEO Launch Pad Tier Image",
        highlights: [
            { description: 'Technical SEO audit + critical fixes implemented' },
            { description: 'Up to 5 pages optimized' },
            { description: 'Google Business Profile setup & optimization' },
            { description: 'Google Search Console and Bing Webmaster setup & optimization' },
            { description: 'Monthly rankings & traffic report' },
        ],
    },
    {
        id: "Growth",
        key: "growth",
        name: 'Growth',
        description: 'Established SMBs wanting content + authority',
        priceMonthly: <div className="">$1<span style={{fontSize: "40px"}}>,997</span></div>,
        cost: "1997",
        href: '/checkout?index=growth',
        imageSrc: "https://images.pexels.com/photos/4050081/pexels-photo-4050081.jpeg",
        imageAlt: "HerStory SEO Growth Accelerator Tier Image",
        highlights: [
            {description: 'Everything in Foundation'},
            {description: '15–20 pages fully optimized'},
            {description: '2 SEO blog posts per month written & published'},
            {description: '2–4 backlinks earned per month'},
            {description: 'Google Analytics setup + conversion optimization'},
            {description: 'Site Speed Optimization'},
            {description: 'Keyword gap analysis'},
            {description: 'Quarterly strategy workshop'},
        ],
    },
    {
        id: "Scale",
        key: "market-leader",
        name: 'Authority',
        description: 'Competitive markets, serious content investment',
        priceMonthly: <div className="">$3<span style={{fontSize: "40px"}}>,800</span></div>,
        cost: "3000",
        href: '/checkout?index=market-leader',
        imageSrc: "https://images.pexels.com/photos/4050081/pexels-photo-4050081.jpeg",
        imageAlt: "HerStory SEO Market-Leader Tier Image",
        highlights: [
            {description: 'Everything in Growth'},
            {description: '30–50 pages optimized'},
            {description: '4–8 content pieces/mo'},
            {description: '5–10 high-authority links/mo'},
            {description: 'Digital PR outreach'},
            {description: 'Google Knowledge Panel'},
            {description: 'GEO (Generative Engine Optimization)'},
            {description: 'Social Media Management - weekly posting schedule'},
            {description: 'SEO competitor analysis'},
            {description: 'Slack Workspace Integration'},
            {description: 'Priority phone support + 1:1 onboarding'},
        ],
    },
]
const sections = [
    {
        name: 'Features',
        features: [
            { name: 'Technical SEO Audit & Fixes', description: "Initial comprehensive audit and correction of critical issues (broken links, site speed, mobile-friendliness, XML sitemap)", tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'On-Page SEO (limited pages)', description: "Optimization of core service/product pages and key landing pages. Includes title tags, meta descriptions, header tags, and content refinement.", tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'Monthly Performance Reporting', description: "A clear report showing rankings, website traffic, and lead metrics.", tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'Advanced Technical SEO', description: "Ongoing site speed optimization, internal linking strategy, canonicalization, and  schema markup implementation.", tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Comprehensive On-Page SEO', description: "Optimization for all key pages on the website (e.g., 15-30 pages - premium 30-50) and ongoing optimization as the site grows.", tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Content Marketing Strategy', description: "Creation and promotion of 2-4 high-quality, SEO-optimized blog posts or articles per month designed to attract and convert your target audience.", tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Strategic Link Building', description: "Active outreach for earning high-quality backlinks from relevant websites (e.g., 2-4 quality links per month - premium 3-7). This is a primary driver for authority.", tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Competitor Gap Analysis', description: "Identify and reverse-engineer the strategies of your top competitors.", tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Enterprise-Level Technical SEO', description: "In-depth log file analysis, JavaScript SEO, international SEO (hreflang), and complex site architecture audits.", tiers: { Starter: false, Growth: false, Scale: true } },
            { name: 'Content at Scale', description: "Development of extensive, in-depth content such as pillar pages, buying guides, and data-driven studies. (e.g., 4-8+ pieces of premium content per month).", tiers: { Starter: false, Growth: false, Scale: true } },
            { name: 'Aggressive & Diversified Link Building', description: "A multi-pronged approach including digital PR, guest posting on top-tier sites, and broken link building to earn 5-10+ high-authority links per month.", tiers: { Starter: false, Growth: false, Scale: true } },
            { name: 'E-commerce SEO', description: "Product page optimization, category page strategy, and faceted navigation handling.", tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Conversion Rate Optimization (CRO) Integration', description: "A/B testing of landing pages and meta data to improve the ROI of organic traffic.", tiers: { Starter: false, Growth: true, Scale: true } },
        ],
    },
    {
        name: 'Local SEO',
        features: [
            { name: 'Google Business Profile Optimization', description: "Full setup and optimization, post management, and review generation strategy.", tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'Local SEO Foundation', description: "Citation building on key directories (e.g., Apple Maps, Bing Places, Yelp) and consistency cleanup.", tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'Basic Keyword Strategy (limited pages)', description: "Targeting a focused set of local and \"long-tail\" keywords.", tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'Advanced Local SEO (PRO)', description: "More aggressive citation building, local link building, and managing multiple locations if applicable.", tiers: { Starter: false, Growth: true, Scale: true } },
            // { name: 'Custom report builder', tiers: { Starter: false, Growth: true, Scale: true } },
        ],
    },
    {
        name: 'Support',
        features: [
            { name: 'Live online support', tiers: { Starter: true, Growth: true, Scale: true } },
            { name: 'Shared Slack Workspace', tiers: { Starter: false, Growth: true, Scale: true } },
            { name: 'Quarterly workshops', tiers: { Starter: false, Growth: true, Scale: true } },
            { name: '1:1 onboarding tour', tiers: { Starter: false, Growth: false, Scale: true } },
            { name: 'Priority phone support', tiers: { Starter: false, Growth: false, Scale: true } },

        ],
    },
]

export default function RetainerPricingComponent() {

    return (

        <main className="bg-black">
            <section className="container container-wide container-single">
                <section id="" className="ns-bio-hero alignfull" style={{background: "#000"}}>
                    <div className="container ns-bio-hero__info">
                        <div className="ns-bio-hero__info__content flex justify-center items-center">
                            <h1 className="custom-font text-center">SEO <div className="pt-4">Retainer</div></h1>
                        </div>
                    </div>
                    <div className="ns-bio-hero__image">
                        <figure>
                            <img decoding="async"
                                 src={RetainerImage}
                                 alt="To build your SEO Today"/>
                        </figure>
                    </div>
                </section>


            </section>

            <div style={{width: "100vw"}}>
                <MainBody />
            </div>
            {/*{viewCartButtonBanner()}*/}
            {/*<CartView isVisible={isCartVisible} setIsVisible={setIsCartVisible}/>*/}

        </main>
    )

    function MainBody() {
        const [showDescription, setShowDescription] = useState(false)
        return (
            <div className=" py-24 sm:py-32 bg-black">
                <div className="mx-auto max-w-4xl px-6 max-lg:text-center lg:max-w-7xl lg:px-8">
                    <h1 className="text-center text-4xl font-semibold tracking-tight text-balance sm:text-6xl lg:text-pretty text-white custom-font">
                        SEO Done-For-You
                    </h1>
                </div>
                <div className="relative pt-16 sm:pt-24">
                    <div
                        className="absolute inset-x-0 top-48 bottom-0 bg-[radial-gradient(circle_at_center_center,#7775D6,#592E71,#030712_70%)] lg:bg-[radial-gradient(circle_at_center_150%,#7775D6,#592E71,#030712_70%)] dark:bg-[radial-gradient(circle_at_center_center,#7775D680,#592E7180,transparent_70%)] dark:lg:bg-[radial-gradient(circle_at_center_150%,#7775D680,#592E7180,transparent_70%)]" />
                    <div className="relative mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
                        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
                            {tiers.map((tier) => (
                                <div
                                    key={tier.name}
                                    className="-m-2 grid grid-cols-1 rounded-4xl bg-white/2.5 max-lg:mx-auto max-lg:w-full max-lg:max-w-md"
                                >
                                    <div className="grid grid-cols-1 rounded-4xl p-2 shadow-md shadow-black/5">
                                        <div
                                            className={`${tier.id === "Growth" ? "ring-2 ring-purple-500" : ""} rounded-3xl bg-gray-900 p-10 pb-9 shadow-2xl`}>
                                            <div className="flex space-x-2 items-center justify-between">
                                                <h2 className="text-4xl font-semibold text-purple-300 custom-font">
                                                    {tier.name}
                                                </h2>

                                                {
                                                    tier.id === "Growth" &&
                                                    <span
                                                        className="h-8 inline-flex items-center rounded-xl bg-purple-50 px-4 py-1 text-xs font-semibold text-purple-500 inset-ring inset-ring-blue-700/10">
                                                    Most popular
                                                </span>
                                                }
                                            </div>
                                            <p className="mt-2 text-sm/6 text-pretty text-gray-600 dark:text-gray-300">{tier.description}</p>
                                            <div className="mt-8 flex items-center gap-4">
                                                <div
                                                    className="text-5xl text-gray-950 dark:text-white flex items-end justify-between custom-font italic">
                                                    {tier.priceMonthly}

                                                    <div className="text-base">
                                                        &nbsp;/month
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex">
                                                <a
                                                    href={tier.href}
                                                    type="button"
                                                    className="mt-4 rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                >
                                                    Get started with {tier.name}
                                                </a>
                                            </div>
                                            <div className="mt-8">
                                                <h3 className="text-sm/6 font-medium text-gray-950 dark:text-white">
                                                    What actually happens:</h3>
                                                <ul className="mt-3 space-y-3">
                                                    {tier.highlights.map((highlight) => (
                                                        <li
                                                            key={highlight.description}
                                                            data-disabled={highlight.disabled}
                                                            className="group flex items-start gap-4 text-sm/6 text-gray-600 data-disabled:text-gray-400 dark:text-gray-300 dark:data-disabled:text-gray-500"
                                                        >
                            <span className="inline-flex h-6 items-center">
                              <PlusIcon
                                  aria-hidden="true"
                                  className="size-4 fill-gray-400 group-data-disabled:fill-gray-300 dark:fill-gray-500 dark:group-data-disabled:fill-gray-600"
                              />
                            </span>
                                                            {highlight.disabled ?
                                                                <span className="sr-only">Not included:</span> : null}
                                                            {highlight.description}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div
                            className="flex justify-between py-16 opacity-60 max-sm:mx-auto max-sm:max-w-md max-sm:flex-wrap max-sm:justify-evenly max-sm:gap-x-4 max-sm:gap-y-4 sm:py-24">
                            <img
                                alt="Ancestral Supplements"
                                src="https://ancestralsupplements.com/cdn/shop/files/Logo-White_210x@2x.png?v=1660943305"
                                className="h-9 max-sm:mx-auto sm:h-8 lg:h-12 object-contain"
                            />
                            <img
                                alt="The Small Business Expo"
                                src="https://d9hhrg4mnvzow.cloudfront.net/cf.thesmallbusinessexpo.com/exhibit-3/40399a5c-smbx-white-logo_10fo068000000000000000.png"
                                className="h-9 max-sm:mx-auto sm:h-8 lg:h-12 object-contain"
                            />
                            <img
                                alt="Williamsburg Charter Sails"
                                src="https://williamsburgchartersails.com/wp-content/uploads/2023/12/williamsburg-charter-sails-logo-reversed.png"
                                className="h-9 max-sm:mx-auto sm:h-8 lg:h-12 object-contain"
                            />
                            <img
                                alt="Print Crazee"
                                src="https://printcrazee.com/wp-content/uploads/2021/09/logo.png"
                                className="h-9 max-sm:mx-auto sm:h-8 lg:h-12 object-contain"
                            />
                            <img
                                alt="Anko Products"
                                src="https://ankoproducts.com/cdn/shop/files/ANKO_LOGO_4_badbfd93-9eea-4b39-9a45-15ce2fd77251_180x@2x.jpg?v=1718989035"
                                className="h-9 max-sm:mx-auto sm:h-8 lg:h-12 object-contain"
                            />
                        </div>
                    </div>
                </div>
                <div className="mx-auto max-w-2xl px-6 pt-16 sm:pt-24 lg:max-w-7xl lg:px-8">
                    <table className="w-full text-left max-sm:hidden">
                        <caption className="sr-only">Pricing plan comparison</caption>
                        <colgroup>
                            <col className="w-2/5" />
                            <col className="w-1/5" />
                            <col className="w-1/5" />
                            <col className="w-1/5" />
                        </colgroup>
                        <thead>
                        <tr>
                            <td className="p-0" />
                            {tiers.map((tier) => (
                                <th key={tier.name} scope="col" className="p-0">
                                    <div className="text-sm font-semibold text-orange-300">
                                        {tier.name}
                                    </div>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th className="p-0" />
                            {tiers.map((tier) => (
                                <td key={tier.name} className="px-0 pt-3 pb-0">
                                    <a
                                        href={tier.href}
                                        aria-label={`Get started with the ${tier.name} plan`}
                                        className="inline-block rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring dark:inset-ring-white/5 dark:hover:bg-white/20"
                                    >
                                        Get started
                                    </a>
                                </td>
                            ))}
                        </tr>
                        </thead>
                        {sections.map((section) => (
                            <tbody key={section.name} className="group">
                            <tr>
                                <th scope="colgroup" colSpan={4} className="px-0 pt-10 pb-0 group-first-of-type:pt-5">
                                    <div className="-mx-4 rounded-lg bg-gray-50 px-4 py-3 text-sm/6 font-semibold text-gray-950 dark:bg-gray-800/50 dark:text-white">
                                        {section.name}
                                    </div>
                                </th>
                            </tr>
                            {section.features.map((feature) => (
                                <tr key={feature.name} className="border-b border-gray-100 last:border-none dark:border-white/10">
                                    <th scope="row"
                                        className="px-0 py-4 text-sm/6 text-white font-semibold">
                                        {feature.name}
                                        <p className="text-sm text-gray-300 font-normal">{feature.description}</p>

                                    </th>
                                    {tiers.map((tier) => (
                                        <td key={tier.name} className="p-4 max-sm:text-center">
                                            {typeof feature.tiers[tier.name] === 'string' ? (
                                                <>
                                                    <span className="sr-only">{tier.name} includes:</span>
                                                    <span
                                                        className="text-sm/6 text-gray-950 dark:text-white">{feature.tiers[tier.name]}</span>
                                                </>
                                            ) : (
                                                <>
                                                    {feature.tiers[tier.id] === true ? (
                                                        <CheckIcon
                                                            aria-hidden="true"
                                                            className="inline-block size-10 fill-green-600 dark:fill-green-500"
                                                        />
                                                    ) : (
                                                        <MinusIcon
                                                            aria-hidden="true"
                                                            className="inline-block size-4 fill-gray-400 dark:fill-gray-500"
                                                        />
                                                    )}

                                                    <span className="sr-only">
                            {feature.tiers[tier.name] === true
                                ? `Included in ${tier.name}`
                                : `Not included in ${tier.name}`}
                          </span>
                                                </>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                        ))}
                    </table>
                    <TabGroup className="sm:hidden">
                        <TabList className="flex">
                            {tiers.map((tier) => (
                                <Tab
                                    key={tier.name}
                                    className="w-1/3 border-b border-gray-100 py-4 text-base/8 font-medium text-indigo-600 not-focus-visible:focus:outline-none data-selected:border-indigo-600 dark:border-white/10 dark:text-indigo-400 dark:data-selected:border-indigo-400"
                                >
                                    {tier.name}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels as={Fragment}>
                            {tiers.map((tier) => (
                                <TabPanel key={tier.name} className="focus:outline-none">
                                    <a
                                        href={tier.href}
                                        className="mt-8 block rounded-md bg-white px-3.5 py-2.5 text-center text-sm font-semibold text-gray-900 shadow-xs inset-ring ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:ring-transparent dark:inset-ring-white/5 dark:hover:bg-white/20"
                                    >
                                        Get started
                                    </a>
                                    {sections.map((section) => (
                                        <Fragment key={section.name}>
                                            <div className="-mx-6 mt-10 rounded-lg bg-gray-50 px-6 py-3 text-sm/6 font-semibold text-gray-950 group-first-of-type:mt-5 dark:bg-gray-800/50 dark:text-white">
                                                {section.name}
                                            </div>
                                            <dl>
                                                {section.features.map((feature) => (
                                                    <div
                                                        key={feature.name}
                                                        className="grid grid-cols-2 border-b border-gray-100 py-4 last:border-none dark:border-white/10"
                                                    >
                                                        <dt className="text-sm/6 font-normal text-gray-600 dark:text-gray-300">{feature.name}</dt>
                                                        <dd className="text-center">
                                                            {typeof feature.tiers[tier.name] === 'string' ? (
                                                                <span className="text-sm/6 text-gray-950 dark:text-white">
                                {feature.tiers[tier.name]}
                              </span>
                                                            ) : (
                                                                <>
                                                                    {feature.tiers[tier.name] === true ? (
                                                                        <CheckIcon
                                                                            aria-hidden="true"
                                                                            className="inline-block size-4 fill-green-600 dark:fill-green-500"
                                                                        />
                                                                    ) : (
                                                                        <MinusIcon
                                                                            aria-hidden="true"
                                                                            className="inline-block size-4 fill-gray-400 dark:fill-gray-500"
                                                                        />
                                                                    )}

                                                                    <span className="sr-only">{feature.tiers[tier.name] === true ? 'Yes' : 'No'}</span>
                                                                </>
                                                            )}
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </Fragment>
                                    ))}
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </TabGroup>
                </div>
            </div>
        )
    }
}
